import { CampoDeData } from "./CampoDeData"
import { Escolha } from "./Escolha"
import { Input } from "./Input"
import { useEffect, useRef, useState, type ReactNode } from "react"

import { ErroDaApi, type MudancaDoCadastro, type Opcao } from "../api"
import { CampoEmLinha, BlocoEditavel, RecusaJaNaTela } from "../ficha/Campos"
import { cnpjValido, cpfValido, data, maiusculaInicial, mascaraDeCep, mascaraDeCnpj, mascaraDeCpf } from "../formato"
import * as Icones from "./Icones"
import { Button } from "./Button"
import { MarcaDeObrigatorio, RotuloDoCampo } from "./RotuloDoCampo"
import { InputError } from "./InputError"
import { MAIUSCULA, ROTULO_DE_CAMPO } from "../lib/classes"
import { cn } from "../lib/cn"

export type CampoDoBloco = {
  campo: string
  rotulo: string
  tipo?: "texto" | "data" | "escolha" | "email" | "telefone" | "cpf" | "cnpj" | "cep" | "endereco"
  opcoes?: Opcao[]
  ajuda?: string
  largo?: boolean
  obrigatorio?: boolean
  rotuloOculto?: boolean
  naoAntesDe?: string
}

const MAXIMO_DO_TIPO: Partial<Record<NonNullable<CampoDoBloco["tipo"]>, number>> = {
  telefone: 15,
  cpf: 14,
  cnpj: 18,
  cep: 9,
}

const MODELO_DO_TIPO: Partial<Record<NonNullable<CampoDoBloco["tipo"]>, string>> = {
  telefone: "(00) 00000-0000",
  cpf: "000.000.000-00",
  cnpj: "00.000.000/0000-00",
  cep: "00000-000",
}

export type ValoresDoBloco = Record<string, string | null | undefined>

export type GradeDoBloco = { leitura: string; formulario: string }

export function mascaraDoTipo(tipo: CampoDoBloco["tipo"], valor: string): string {
  if (valor.includes("*")) return valor
  if (tipo === "telefone") return mascaraDeTelefone(valor)
  if (tipo === "cpf") return mascaraDeCpf(valor)
  if (tipo === "cnpj") return mascaraDeCnpj(valor)
  if (tipo === "cep") return mascaraDeCep(valor)
  return valor
}

function comMascara(campos: CampoDoBloco[], valores: ValoresDoBloco): ValoresDoBloco {
  const resultado: ValoresDoBloco = { ...valores }
  for (const { campo, tipo } of campos) {
    const valor = valores[campo]
    if (typeof valor === "string" && valor !== "") resultado[campo] = mascaraDoTipo(tipo, valor)
  }
  return resultado
}

export function valorNaTela(campo: CampoDoBloco, valor: string | null | undefined): ReactNode {
  if (valor === null || valor === undefined || valor === "") return "—"
  if (campo.tipo === "telefone" || campo.tipo === "cpf" || campo.tipo === "cnpj" || campo.tipo === "cep") {
    return mascaraDoTipo(campo.tipo, valor)
  }
  if (campo.tipo === "data") return data(valor)
  if (campo.tipo === "escolha") {
    return campo.opcoes?.find((o) => o.chave === valor)?.rotulo ?? valor
  }
  return valor
}

const comMaiuscula = maiusculaInicial

export function mensagemDeRecusa(falha: unknown): string {
  if (falha instanceof ErroDaApi) {
    const partes = [comMaiuscula(falha.regra)]
    if (falha.acao !== "") partes.push(comMaiuscula(falha.acao))
    const texto = partes.join(". ")
    return falha.referencia !== null ? `${texto} Referência: ${falha.referencia}.` : texto
  }
  return comMaiuscula(falha instanceof Error ? falha.message : "Não foi possível salvar")
}

export function mascaraDeTelefone(valor: string): string {
  const d = valor.replace(/\D/g, "").slice(0, 11)
  if (d.length === 0) return ""
  if (d.length <= 2) return `(${d}`
  const meio = d.length === 11 ? 7 : 6
  if (d.length <= meio) return `(${d.slice(0, 2)}) ${d.slice(2)}`
  return `(${d.slice(0, 2)}) ${d.slice(2, meio)}-${d.slice(meio)}`
}

const FORMA_DO_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function recusaDoCampo(
  campo: CampoDoBloco,
  valor: string | null | undefined,
  valores: ValoresDoBloco = {},
): string | null {
  const texto = (valor ?? "").trim()
  if (texto === "") return null
  if (campo.naoAntesDe !== undefined) {
    const anterior = (valores[campo.naoAntesDe] ?? "").trim()
    if (anterior !== "" && texto < anterior) return "O término não pode ser antes do início."
  }
  if (campo.tipo === "telefone") {
    const algarismos = texto.replace(/\D/g, "").length
    if (algarismos < 10 || algarismos > 11) return "DDD e número: 10 ou 11 dígitos."
  }
  if (campo.tipo === "cpf" && !cpfValido(texto)) {
    return "Confira o CPF: os dígitos verificadores não conferem."
  }
  if (campo.tipo === "cnpj" && !cnpjValido(texto)) {
    return "Confira o CNPJ: os dígitos verificadores não conferem."
  }
  if (campo.tipo === "cep" && texto.replace(/\D/g, "").length !== 8) {
    return "O CEP tem 8 dígitos."
  }
  if (campo.tipo === "email" && !FORMA_DO_EMAIL.test(texto)) {
    return "Formato: nome@orgao.gov.br."
  }
  return null
}

export function diferenca(
  campos: CampoDoBloco[],
  originais: ValoresDoBloco,
  rascunho: ValoresDoBloco,
): MudancaDoCadastro {
  const mudanca: MudancaDoCadastro = {}
  for (const { campo, tipo } of campos) {
    const antes = mascaraDoTipo(tipo, originais[campo] ?? "")
    const depois = mascaraDoTipo(tipo, rascunho[campo] ?? "")
    if (antes !== depois) mudanca[campo] = depois === "" ? null : depois
  }
  return mudanca
}

export function FormularioDeCampos({
  prefixo,
  campos,
  inicial,
  aoMudar,
  conferir = false,
  grade,
  naGaveta = false,
  recusasDeFora = {},
}: {
  prefixo: string
  campos: CampoDoBloco[]
  inicial: ValoresDoBloco
  aoMudar: (valores: ValoresDoBloco) => void
  conferir?: boolean
  grade?: GradeDoBloco
  naGaveta?: boolean
  recusasDeFora?: Record<string, string>
}) {
  const [valores, definirValores] = useState<ValoresDoBloco>(() => comMascara(campos, inicial))
  const temConferencia =
    Object.keys(recusasDeFora).length > 0 ||
    campos.some((c) => c.naoAntesDe !== undefined || c.tipo === "telefone" || c.tipo === "email" || c.tipo === "cpf" || c.tipo === "cnpj" || c.tipo === "cep")
  useEffect(() => {
    aoMudar(comMascara(campos, inicial))
  }, [])

  const mudar = (campo: string, valor: string) => {
    const novos = { ...valores, [campo]: valor }
    definirValores(novos)
    aoMudar(novos)
  }

  return (
    <div
      data-slot="formulario-embutido"
      className={naGaveta ? undefined : "mt-3 rounded-(--raio-controle) border border-(--borda-forte) bg-(--superficie) p-3.5"}
    >
      <div
        data-slot={grade === undefined ? "linha-de-campos" : undefined}
        className={grade?.formulario ?? "flex flex-wrap items-end gap-3"}
      >
        {campos.map((campo) => {
          const id = `${prefixo}-${campo.campo}`
          const valor = valores[campo.campo] ?? ""
          const recusa =
            recusasDeFora[campo.campo] ??
            (conferir && (campo.naoAntesDe !== undefined || valor !== (inicial[campo.campo] ?? ""))
              ? recusaDoCampo(campo, valor, valores)
              : null)
          return (
            <div key={campo.campo} className={cn("flex-[1_1_240px]", campo.largo && "basis-full")}>
              {campo.rotuloOculto === true ? (
                <label className="sr-only" htmlFor={id}>
                  {campo.rotulo}
                </label>
              ) : naGaveta ? (
                <RotuloDoCampo htmlFor={id} obrigatorio={campo.obrigatorio === true}>
                  {campo.rotulo}
                </RotuloDoCampo>
              ) : (
                <div data-slot="rotulo-do-campo" className="flex items-center gap-1">
                  <label className={ROTULO_DE_CAMPO} htmlFor={id}>
                    {campo.rotulo}
                  </label>
                  {campo.obrigatorio === true && <MarcaDeObrigatorio />}
                </div>
              )}
              {campo.tipo === "escolha" ? (
                <Escolha
                  id={id}
                  name={id}
                  obrigatorio={campo.obrigatorio === true}
                  valor={valor}
                  aoMudar={(novo) => mudar(campo.campo, novo)}
                  vazio="—"
                  opcoes={(campo.opcoes ?? []).map((opcao) => ({ valor: opcao.chave, rotulo: opcao.rotulo }))}
                />
              ) : campo.tipo === "data" ? (
                <CampoDeData
                  id={id}
                  name={id}
                  obrigatorio={campo.obrigatorio === true}
                  valor={valor}
                  aoMudar={(novo) => mudar(campo.campo, novo)}
                />
              ) : (
                <Input
                  id={id}
                  name={id}
                  required={campo.obrigatorio === true}
                  type={campo.tipo === "email" ? "email" : campo.tipo === "telefone" ? "tel" : "text"}
                  maiusculas={campo.tipo !== "endereco"}
                  inputMode={campo.tipo === "telefone" || campo.tipo === "cpf" || campo.tipo === "cep" ? "numeric" : undefined}
                  maxLength={MAXIMO_DO_TIPO[campo.tipo ?? "texto"]}
                  placeholder={MODELO_DO_TIPO[campo.tipo ?? "texto"]}
                  autoComplete="off"
                  data-form-type="other"
                  aria-invalid={recusa !== null || undefined}
                  aria-describedby={recusa !== null ? `${id}-recusa` : undefined}
                  value={valor}
                  onChange={(evento) =>
                    mudar(
                      campo.campo,
                      campo.tipo === "email"
                        ? evento.target.value.replace(/\s/g, "")
                        : mascaraDoTipo(campo.tipo, evento.target.value),
                    )
                  }
                />
              )}
              {temConferencia && (!naGaveta || recusa !== null) && (
                <div className={naGaveta ? "mt-1" : "mt-1 min-h-5"}>
                  <InputError id={`${id}-recusa`} message={recusa ?? undefined} />
                </div>
              )}
              {campo.ajuda !== undefined && (
                <p className={cn("mt-1 text-(length:--texto-sm) text-(--tinta-3)", MAIUSCULA)}>{campo.ajuda}</p>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export function faltaNoBloco(campos: CampoDoBloco[], valores: ValoresDoBloco): string | null {
  const vazio = campos.find((campo) => campo.obrigatorio === true && (valores[campo.campo] ?? "").trim() === "")
  return vazio === undefined ? null : `Falta preencher: ${vazio.rotulo}`
}

export function BlocoDeCampos({
  prefixo,
  titulo,
  recado,
  campos,
  valores,
  editavel,
  motivoSemEdicao,
  aoGravar,
  depois,
  grade,
}: {
  prefixo: string
  titulo: string
  recado?: ReactNode
  campos: CampoDoBloco[]
  valores: ValoresDoBloco
  editavel: boolean
  motivoSemEdicao?: string
  aoGravar: (mudanca: MudancaDoCadastro) => Promise<void>
  depois?: ReactNode
  grade?: GradeDoBloco
}) {
  const rascunho = useRef<ValoresDoBloco>(valores)
  const [conferir, definirConferir] = useState(false)
  const [falta, definirFalta] = useState<string | null>(null)
  useEffect(() => {
    rascunho.current = valores
  }, [valores])

  return (
    <BlocoEditavel
      titulo={titulo}
      recado={recado}
      editavel={editavel}
      motivoSemEdicao={motivoSemEdicao}
      falta={falta}
      leitura={
        <>
          <div
            data-slot={grade === undefined ? "campos-em-linha" : undefined}
            className={grade?.leitura ?? "flex flex-col"}
          >
            {campos.map((campo) => (
              <CampoEmLinha
                key={campo.campo}
                rotulo={campo.rotulo}
                valor={valorNaTela(campo, valores[campo.campo])}
                longo={campo.largo}
              />
            ))}
          </div>
          {depois}
        </>
      }
      formulario={() => (
        <FormularioDeCampos
          prefixo={prefixo}
          campos={campos}
          inicial={valores}
          aoMudar={(novos) => {
            rascunho.current = novos
            definirFalta(faltaNoBloco(campos, novos))
          }}
          conferir={conferir}
          grade={grade}
        />
      )}
      aoGravar={async () => {
        definirConferir(true)
        const recusado = campos.some((campo) => {
          const novo = rascunho.current[campo.campo] ?? ""
          const conferido = campo.naoAntesDe !== undefined || novo !== (valores[campo.campo] ?? "")
          return conferido && recusaDoCampo(campo, novo, rascunho.current) !== null
        })
        if (recusado) throw new RecusaJaNaTela()
        const mudanca = diferenca(campos, valores, rascunho.current)
        if (Object.keys(mudanca).length === 0) return definirConferir(false)
        await aoGravar(mudanca)
        definirConferir(false)
      }}
    />
  )
}

export function useImagemProtegida(
  buscar: () => Promise<Blob | null>,
  versao: string,
): { endereco: string | null; carregando: boolean } {
  const [endereco, definirEndereco] = useState<string | null>(null)
  const [carregando, definirCarregando] = useState(true)
  const buscarAtual = useRef(buscar)
  buscarAtual.current = buscar

  useEffect(() => {
    let vivo = true
    let criado: string | null = null
    definirCarregando(true)
    buscarAtual
      .current()
      .then((blob) => {
        if (!vivo) return
        if (blob !== null) {
          criado = URL.createObjectURL(blob)
          definirEndereco(criado)
        } else {
          definirEndereco(null)
        }
      })
      .catch(() => {
        if (vivo) definirEndereco(null)
      })
      .finally(() => {
        if (vivo) definirCarregando(false)
      })
    return () => {
      vivo = false
      if (criado !== null) URL.revokeObjectURL(criado)
    }
  }, [versao])

  return { endereco, carregando }
}

export function EnviarImagem({
  rotulo,
  nome,
  aoEnviar,
}: {
  rotulo: string
  nome: string
  aoEnviar: (arquivo: File) => Promise<void>
}) {
  const [enviando, definirEnviando] = useState(false)
  return (
    <Button
      asChild
      variant="outline"
      size="sm"
      className={enviando ? "cursor-progress text-muted-foreground" : undefined}
    >
      <label>
        <Icones.Enviar />
        {enviando ? "Enviando..." : rotulo}
        <input
          type="file"
          name={nome}
          accept="image/png,image/jpeg"
          hidden
          disabled={enviando}
          onChange={(evento) => {
            const arquivo = evento.target.files?.[0]
            evento.target.value = ""
            if (arquivo === undefined) return
            definirEnviando(true)
            void aoEnviar(arquivo).finally(() => definirEnviando(false))
          }}
        />
      </label>
    </Button>
  )
}
